import { SUB_APPS, DEFAULT_SUB_APP_ID } from '../config/sub-apps';
import type { SubApp, SubAppId } from '../model/types';

/**
 * The sub-apps that render on a launcher surface — everything not `hidden`,
 * in catalog order. Both the welcome picker and the header switcher call this,
 * so a `hidden` entry disappears from the whole app at once.
 */
export function visibleSubApps(catalog: readonly SubApp[] = SUB_APPS): SubApp[] {
  return catalog.filter((app) => app.status !== 'hidden');
}

/** Whether a sub-app can be navigated into. `soon` and `hidden` cannot. */
export function isNavigable(app: SubApp): app is SubApp & { path: string } {
  return app.status === 'available' && app.path !== undefined;
}

/** Whether a launcher card shows the "Скоро" tag: not built yet, or open but flagged. */
export function showsSoonTag(app: SubApp): boolean {
  return app.status === 'soon' || app.badge === 'soon';
}

/**
 * Where to send a merchant who lands on the app without choosing a sub-app.
 * Prefers the configured default, falling back to the first navigable entry so
 * the app never dead-ends if the default is turned off. Returns `undefined`
 * when nothing is navigable at all — the caller decides what that means (the
 * welcome page just renders every tile inert).
 */
export function defaultSubAppPath(
  catalog: readonly SubApp[] = SUB_APPS,
  defaultId: SubAppId = DEFAULT_SUB_APP_ID,
): string | undefined {
  const preferred = catalog.find((app) => app.id === defaultId);
  if (preferred && isNavigable(preferred)) return preferred.path;

  return catalog.find(isNavigable)?.path;
}

/** Which sub-app a pathname belongs to, or `undefined` for a chromeless route. */
export function subAppForPath(
  pathname: string,
  catalog: readonly SubApp[] = SUB_APPS,
): SubApp | undefined {
  return catalog.find(
    (app) =>
      app.path !== undefined &&
      (pathname === app.path || pathname.startsWith(`${app.path}/`)),
  );
}

/**
 * Routes that belong to NO sub-app but still render inside whichever shell the
 * merchant came from — the profile screen and support.
 *
 * They are merchant-wide: one profile, one staff roster and one support thread
 * serve Eats and Доставка alike, so scoping them to either would imply a
 * per-sub-app copy that does not exist.
 */
export const SHARED_PATHS = ['/profile', '/support'] as const;

/**
 * Whether `pathname` is a shared route (or nested under one).
 *
 * Matched by PREFIX, not equality, and that distinction is the whole point:
 * `/profile` owns sub-routes — `/profile/users`, the staff roster — and an
 * exact-match test let those render with the surrounding sub-app's own name
 * and nav tabs, advertising a section the screen does not belong to.
 */
export function isSharedPath(pathname: string): boolean {
  return SHARED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
